import {
  FormHelperText,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
  outlinedInputClasses,
  styled,
} from "@mui/material"
import type { StackProps } from "@mui/material/Stack/Stack"
import {
  type HTMLInputTypeAttribute,
  type KeyboardEvent,
  useMemo,
  useRef,
  useState,
} from "react"
import { Eye, EyeOff } from "react-feather"
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form"
import { ActionButton, type ActionButtonProps } from "../../../navigation"
import { INPUT_LENGTH } from "./pinDialog.const"

export type FormPinInputsProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>
  defaultInputType?: HTMLInputTypeAttribute
  label?: string
  slotProps?: {
    root?: StackProps
    pins?: Omit<
      TextFieldProps,
      "error" | "inputRef" | "onKeyDown" | "type" | "value" | "onClick" | "slotProps"
    >
    button?: Omit<ActionButtonProps, "Icon" | "onClick">
  }
}

export const FormPinInputs = <TFieldValues extends FieldValues = FieldValues>({
  name,
  defaultInputType = "password",
  slotProps = {},
  label,
}: FormPinInputsProps<TFieldValues>) => {
  const { setValue } = useFormContext<TFieldValues>()
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  /**
   * Handles keyboard input for PIN dialog inputs
   *
   * Filters input to only allow digits (0-9) and backspace key.
   * Manages the PIN password string by either appending digits or removing
   * characters on backspace. Automatically focuses the next available input
   * field and enforces maximum length constraint.
   *
   * Note: This handler does not handle paste from clipboard operations.
   * Only individual keystrokes are processed.
   *
   * @param event - Keyboard event from the input field
   * @param index - index
   * @param password - Password field value
   */
  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number,
    password = "",
  ) => {
    if (!event.key.match(new RegExp(/\d/, "g")) && event.key !== "Backspace") {
      return
    }
    let newPassword = ""
    let indexToFocus: number

    if (event.key === "Backspace") {
      newPassword = Array.from({ length: INPUT_LENGTH })
        .map((_, i) => {
          if (i === index) {
            return ""
          }
          return password.charAt(i)
        })
        .join("")
      indexToFocus = Math.max(index - 1, 0)
    } else {
      newPassword = Array.from({ length: INPUT_LENGTH })
        .map((_, i) => {
          if (i === index) {
            return event.key
          }
          return password.charAt(i)
        })
        .join("")
      indexToFocus = Math.min(index + 1, INPUT_LENGTH)
    }

    inputRefs.current[indexToFocus]?.focus()
    // @ts-expect-error string is allowed
    setValue(name, newPassword.slice(0, INPUT_LENGTH))
  }

  const [inputType, setInputType] =
    useState<HTMLInputTypeAttribute>(defaultInputType)
  const toggleInputType = () =>
    setInputType((type) => (type === "password" ? "text" : "password"))
  const Icon = useMemo(() => (inputType === "password" ? Eye : EyeOff), [inputType])

  return (
    <Controller<TFieldValues>
      name={name}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
        <Stack style={{ width: "min-content" }} gap={0.5}>
          {label && <PinInputLabel>{label}</PinInputLabel>}
          <PinInputContainer {...(slotProps?.root ?? {})}>
            {Array.from({ length: INPUT_LENGTH }).map((_, index) => (
              <PinInput
                key={index}
                error={!!error}
                inputRef={(el) => {
                  inputRefs.current[index] = el
                }}
                onKeyDown={(evt) => handleKeyDown(evt, index, value)}
                type={inputType}
                value={(value as string)?.charAt(index) ?? ""}
                slotProps={{
                  htmlInput: {
                    // autoComplete is set to search to avoid password autocompletion from browser
                    autoComplete: "search",
                    pattern: "[0-9]+",
                    inputMode: "numeric",
                  },
                }}
                {...(slotProps?.pins ?? {})}
              />
            ))}
            <input {...field} value={value ?? ""} type="hidden" />
            <ActionButton
              Icon={Icon}
              onClick={toggleInputType}
              {...(slotProps?.button ?? {})}
            />
          </PinInputContainer>
          <ErrorText error={!!error}>{error?.message}</ErrorText>
        </Stack>
      )}
    />
  )
}

const ErrorText = styled(FormHelperText)(({ theme }) => ({
  ...theme.typography["12px"],
  minHeight: theme.typography.pxToRem(12),
  marginLeft: 0,
}))

const PinInput = styled(TextField)(() => ({
  width: 50,
  overflow: "hidden",
  [`& .${outlinedInputClasses.root}`]: {
    height: 50,
  },
  [`& .${outlinedInputClasses.input}`]: {
    textAlign: "center",
    fontSize: "1.5rem",
    maxHeight: "100%",
    padding: 0,

    '&[type="password"]': {
      fontSize: "3rem",
    },
  },
}))

const PinInputContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(2),
  alignItems: "center",
  overflow: "hidden",
}))

const PinInputLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
}))
